import React, { useEffect, useState } from "react";
import { FaUserCheck } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const TASK_TYPES = [
  { key: "HOMEWORK",          label: "Homework" },
  { key: "QUIZ",              label: "Quiz" },
  { key: "WRITING_TASK",      label: "Writing Task" },
  { key: "CLASS_TEST",        label: "Class Test" },
  { key: "SPEAKING_PRACTICE", label: "Speaking Practice" },
] as const;

const WEEKS = Array.from({ length: 16 }, (_, i) => i + 1);

const weekLabel = (startingDate: string | null, weekNum: number): string => {
  if (!startingDate) return `W${weekNum}`;
  const d = new Date(startingDate);
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

interface CheckinDto {
  weekNumber: number;
  taskType: string;
  checked: boolean;
}

interface BatchTrackerDto {
  batchId: number;
  batchName: string;
  startingDate: string | null;
  active: boolean;
  checkins: CheckinDto[];
}

interface MentorTrackerDto {
  mentorId: number;
  mentorName: string;
  batches: BatchTrackerDto[];
}

const buildCheckinMap = (checkins: CheckinDto[]): Map<string, boolean> => {
  const map = new Map<string, boolean>();
  checkins.forEach((c) => {
    if (c.checked) map.set(`${c.weekNumber}_${c.taskType}`, true);
  });
  return map;
};

const completionPct = (checkins: CheckinDto[], totalCells: number): number => {
  if (totalCells === 0) return 0;
  const checked = checkins.filter((c) => c.checked).length;
  return Math.round((checked / totalCells) * 100);
};

const pctColor = (pct: number) => {
  if (pct >= 80) return "text-green-600";
  if (pct >= 40) return "text-yellow-600";
  return "text-red-500";
};

const barColor = (pct: number) => {
  if (pct >= 80) return "bg-green-500";
  if (pct >= 40) return "bg-yellow-400";
  return "bg-red-400";
};

const API = process.env.REACT_APP_API_BASE_URL;

const AdminMentorTracker: React.FC = () => {
  const [data, setData] = useState<MentorTrackerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedMentor, setExpandedMentor] = useState<number | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/mentor-tracker`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch tracker data");
      const json: MentorTrackerDto[] = await res.json();
      const filtered = json
        .map((m) => ({ ...m, batches: m.batches.filter((b) => b.active) }))
        .filter((m) => m.batches.length > 0);
      setData(filtered);
      if (filtered.length > 0 && expandedMentor === null) setExpandedMentor(filtered[0].mentorId);
    } catch (e: any) {
      setError(e.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleMentor = (id: number) =>
    setExpandedMentor((prev) => (prev === id ? null : id));

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        icon={<FaUserCheck />}
        title="Mentor Assignment Tracker"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Mentor Tracker" },
        ]}
      />

      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6 space-y-3 sm:space-y-4">
        {loading && (
          <div className="text-center py-16 text-gray-500">Loading tracker data…</div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="text-center py-16 text-gray-400">No mentors found.</div>
        )}

        {!loading && data.map((mentor) => {
          const isOpen = expandedMentor === mentor.mentorId;
          const allCheckins = mentor.batches.flatMap((b) => b.checkins);
          const totalCells = mentor.batches.reduce(
            (sum, _b) => sum + TASK_TYPES.length * WEEKS.length, 0
          );
          const pct = completionPct(allCheckins, totalCells);

          return (
            <div key={mentor.mentorId} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Mentor header row */}
              <button
                onClick={() => toggleMentor(mentor.mentorId)}
                className="w-full flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#009fe3] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {mentor.mentorName.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left min-w-0">
                    <div className="font-semibold text-gray-800 text-sm sm:text-base truncate">{mentor.mentorName}</div>
                    <div className="text-xs text-gray-500">
                      {mentor.batches.length} batch{mentor.batches.length !== 1 ? "es" : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-6 flex-shrink-0">
                  <div className="text-right">
                    <div className={`text-sm sm:text-lg font-bold ${pctColor(pct)}`}>{pct}%</div>
                    <div className="w-16 sm:w-24 h-1.5 bg-gray-200 rounded-full mt-1">
                      <div
                        className={`h-1.5 rounded-full transition-all ${barColor(pct)}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
                    fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Expanded: batch tables */}
              {isOpen && (
                <div className="border-t border-gray-100 px-2 sm:px-6 py-3 sm:py-4 space-y-5 sm:space-y-6">
                  {mentor.batches.length === 0 && (
                    <p className="text-sm text-gray-400">No batches assigned to this mentor.</p>
                  )}
                  {mentor.batches.map((batch) => {
                    const checkinMap = buildCheckinMap(batch.checkins);
                    const batchPct = completionPct(
                      batch.checkins,
                      TASK_TYPES.length * WEEKS.length
                    );

                    return (
                      <div key={batch.batchId}>
                        <div className="flex items-start sm:items-center justify-between mb-2 sm:mb-3 gap-2">
                          <div className="min-w-0">
                            <span className="font-semibold text-gray-700 text-sm truncate block sm:inline">{batch.batchName}</span>
                            {batch.startingDate && (
                              <span className="sm:ml-2 text-xs text-gray-400 block sm:inline">
                                Started {new Date(batch.startingDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <span className={`text-xs sm:text-sm font-semibold flex-shrink-0 ${pctColor(batchPct)}`}>
                            {batchPct}%
                          </span>
                        </div>

                        {/* Scroll hint (mobile only) */}
                        <p className="sm:hidden text-[10px] text-gray-400 text-center mb-1">
                          ← scroll to see all weeks →
                        </p>

                        {/* Table */}
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                          <table className="text-xs border-collapse w-full" style={{ minWidth: "max-content" }}>
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="sticky left-0 bg-gray-50 z-10 px-2 sm:px-3 py-2 text-left font-semibold text-gray-600 border-b border-r border-gray-200 min-w-[90px] sm:min-w-[130px] shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)]">
                                  Task
                                </th>
                                {WEEKS.map((w) => (
                                  <th
                                    key={w}
                                    className="px-1 sm:px-2 py-2 text-center font-medium text-gray-500 border-b border-r border-gray-100 min-w-[42px] sm:min-w-[48px]"
                                  >
                                    {weekLabel(batch.startingDate, w)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {TASK_TYPES.map((task, idx) => (
                                <tr key={task.key} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                                  <td className="sticky left-0 bg-inherit z-10 px-2 sm:px-3 py-1.5 sm:py-2 font-medium text-gray-700 border-r border-gray-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)] whitespace-nowrap">
                                    {task.label}
                                  </td>
                                  {WEEKS.map((w) => {
                                    const ticked = checkinMap.get(`${w}_${task.key}`) ?? false;
                                    return (
                                      <td key={w} className="px-0.5 sm:px-1 py-1 sm:py-1.5 text-center border-r border-gray-100">
                                        {ticked ? (
                                          <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-100 text-green-600 text-xs font-bold mx-auto">
                                            ✓
                                          </span>
                                        ) : (
                                          <span className="inline-block w-5 h-5 sm:w-6 sm:h-6 rounded-full border border-gray-200 bg-gray-50 mx-auto" />
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminMentorTracker;
